/** Bulk downloads: stream a ZIP in the browser (client-zip) straight to disk when the File System Access API exists. */

export const BROWSER_ZIP_LIMIT = 2 * 1024 ** 3;

export interface ZipSource {
  name: string;
  url: string;
  bytes: number;
}

interface SaveFilePickerWindow {
  showSaveFilePicker?: (opts: {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<FileSystemFileHandle>;
}

/** A place to write the ZIP: a file handle (picked during the click) or an in-memory Blob fallback. */
export type SaveTarget = { kind: 'handle'; handle: FileSystemFileHandle; name: string } | { kind: 'blob'; name: string };

/**
 * Ask where to save. Must be called synchronously from the click handler (user activation).
 * Returns null if the user cancelled the picker.
 */
export async function chooseSaveTarget(name: string): Promise<SaveTarget | null> {
  const w = window as unknown as SaveFilePickerWindow;
  if (typeof w.showSaveFilePicker !== 'function') return { kind: 'blob', name };
  try {
    const handle = await w.showSaveFilePicker({
      suggestedName: name,
      types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
    });
    return { kind: 'handle', handle, name: handle.name };
  } catch (e) {
    if ((e as Error).name === 'AbortError') return null;
    return { kind: 'blob', name };
  }
}

/** Make file names unique inside the ZIP ("IMG_1.JPG", "IMG_1 (2).JPG"). */
export function uniqueNames(names: readonly string[]): string[] {
  const used = new Map<string, number>();
  return names.map((n) => {
    const key = n.toLowerCase();
    const count = used.get(key) ?? 0;
    used.set(key, count + 1);
    if (!count) return n;
    const dot = n.lastIndexOf('.');
    const candidate = dot > 0 ? `${n.slice(0, dot)} (${count + 1})${n.slice(dot)}` : `${n} (${count + 1})`;
    used.set(candidate.toLowerCase(), 1);
    return candidate;
  });
}

export function triggerBlobDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Start a direct download of one URL (signed URLs carry Content-Disposition; `download` helps same-origin URLs). */
export function triggerUrlDownload(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Fetch every file and stream them into a ZIP at `target`. `onProgress` gets bytes read so far and the file number. */
export async function streamZip(
  files: readonly ZipSource[],
  target: SaveTarget,
  onProgress: (loaded: number, file: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const { downloadZip } = await import('client-zip');
  const names = uniqueNames(files.map((f) => f.name));
  let loaded = 0;

  async function* inputs() {
    for (let i = 0; i < files.length; i++) {
      const f = files[i]!;
      const res = await fetch(f.url, { signal });
      if (!res.ok || !res.body) throw new Error(`Couldn’t fetch ${f.name} (${res.status})`);
      const reader = res.body.getReader();
      const counted = new ReadableStream<Uint8Array>({
        async pull(controller) {
          const { done, value } = await reader.read();
          if (done) {
            controller.close();
            return;
          }
          loaded += value.byteLength;
          onProgress(loaded, i + 1);
          controller.enqueue(value);
        },
        cancel(reason) {
          return reader.cancel(reason);
        },
      });
      yield { name: names[i]!, input: counted, lastModified: new Date() };
    }
  }

  const zip = downloadZip(inputs());
  if (!zip.body) throw new Error('Your browser can’t create ZIP files.');
  if (target.kind === 'handle') {
    const writable = await target.handle.createWritable();
    await zip.body.pipeTo(writable, { signal });
  } else {
    const blob = await zip.blob();
    triggerBlobDownload(blob, target.name);
  }
}
